import React, { ChangeEvent, RefObject } from 'react';

import { useIntl } from 'react-intl';
import styled from 'styled-components';

import Checkbox from 'components/checkbox';
import { FloatingLabelInput } from 'components/floating-label-fields';
import { DisclaimerPlain } from 'components/legal-disclaimers/disclaimer';
import { UserDetails } from 'state/auth/hooks/types';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { IPlaceAddress } from 'utils/geolocation';

import PhoneNumberField from '../phone-number-field';
import { IPhoneAddressValidation } from '../types';

const SaveAddressLabel = styled.span`
  line-height: 1.25rem;
`;

const DeliveryFloatingInput = styled(FloatingLabelInput)`
  z-index: 0;
`;
const DeliveryPhoneNumberInput = styled(PhoneNumberField)`
  z-index: 0;
`;

type CallbackState = (saveAddress: boolean) => boolean;

interface IDeliveryFormAdditionalFieldsProps {
  phoneNumber: string;
  setPhoneNumber: (phoneNumber: string) => void;
  addressComponents: IPlaceAddress | null;
  setAddressComponents: (addressComponents: IPlaceAddress) => void;
  deliveryInstructions: string;
  setDeliveryInstructions: (instructions: string) => void;
  user?: UserDetails | null;
  validation: IPhoneAddressValidation;
  phoneNumberError: string;
  unitRef: RefObject<HTMLInputElement>;
  shouldSaveAddress: boolean;
  setShouldSaveAddress: (callbackState: CallbackState) => void;
}

// Note: These fields are disabled by ENABLE_SIMPLE_DELIVERY being true
const DeliveryAddressFormAdditionalFields: React.FC<IDeliveryFormAdditionalFieldsProps> = ({
  phoneNumber,
  setPhoneNumber,
  addressComponents,
  setAddressComponents,
  deliveryInstructions,
  setDeliveryInstructions,
  user,
  validation,
  phoneNumberError,
  unitRef,
  shouldSaveAddress,
  setShouldSaveAddress,
}) => {
  const { formatMessage } = useIntl();
  const enableUserSavedDeliveryAddressPhone = useFlag(
    LaunchDarklyFlag.ENABLE_USER_SAVED_DELIVERY_ADDRESS_PHONE
  );

  const handleUnitNumberChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (addressComponents) {
      setAddressComponents({
        ...addressComponents,
        addressLine2: e.target.value,
      });
    }
  };

  const handlePhoneNumberChange = (e: ChangeEvent<HTMLInputElement>) => {
    setPhoneNumber(e.target.value);
  };

  const handleDeliveryInstructionsChange = (e: ChangeEvent<HTMLInputElement>) => {
    setDeliveryInstructions(e.target.value);
  };

  const shouldEnableUserSavedDeliveryAddressPhone = enableUserSavedDeliveryAddressPhone && !!user;

  return (
    <>
      <DeliveryFloatingInput
        ref={unitRef}
        label={formatMessage({ id: 'unit' })}
        name="unitDetails"
        type="text"
        value={addressComponents?.addressLine2 || ''}
        onChange={handleUnitNumberChange}
      />
      <DeliveryPhoneNumberInput
        label={formatMessage({ id: 'phoneNumber' })}
        name="phoneNumber"
        value={phoneNumber}
        validation={validation.phoneNumber}
        message={phoneNumberError}
        onChange={handlePhoneNumberChange}
        required
      />
      <DeliveryFloatingInput
        label={formatMessage({ id: 'deliveryInstructions' })}
        name="deliveryInstructions"
        type="text"
        maxLength={512}
        value={deliveryInstructions}
        onChange={handleDeliveryInstructionsChange}
      />
      {shouldEnableUserSavedDeliveryAddressPhone && (
        <Checkbox
          data-testid="save-address-checkbox"
          label={
            <SaveAddressLabel>
              {formatMessage({ id: 'saveInformationToMyAccount' })}
            </SaveAddressLabel>
          }
          name="saveAddressToAccount"
          checked={shouldSaveAddress}
          onChange={() => setShouldSaveAddress((saveAddress: boolean) => !saveAddress)}
        />
      )}
      <DisclaimerPlain>{formatMessage({ id: 'deliveryPhoneDisclosure' })}</DisclaimerPlain>
    </>
  );
};

export default DeliveryAddressFormAdditionalFields;
